import { useState, useRef, useEffect } from "react";
import { Container, Typography, Box, Button, Stack, Modal, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import SectionHeader from "../components/SectionHeader";

const s3BaseUrl = "https://djinni-assets.s3.us-east-1.amazonaws.com/gallery/";
const cleaningJobsImages = [
  "1.jpg", "2.jpg", "3.jpg", "4.jpg", "5.jpg", "6.jpg", "7.jpg", "8.jpg", "9.jpg", "10.jpg",
  "11.jpg", "12.jpg", "13.jpg", "14.jpg", "15.jpg", "16.jpg", "17.jpg", "18.jpg", "19.jpg",
  "20.jpg", "21.jpg", "22.jpg", "23.jpg", "24.jpg", "25.jpg", "26.jpg", "27.jpg", "28.jpg",
  "29.jpg", "30.jpg", "31.jpg", "32.jpg", "33.jpg"
];

interface JobsCarouselProps {
  bgcolor?: string;
}

function CleaningJobsCarousel({ bgcolor }: JobsCarouselProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleOpen = (image: string) => {
    setSelectedImage(image);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const scrollToCard = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = isMobile ? container.offsetWidth : container.offsetWidth / 3;
      const newScrollLeft = direction === "left" 
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = isMobile ? container.offsetWidth : container.offsetWidth / 3;
      const scrollPosition = container.scrollLeft;
      const index = Math.round(scrollPosition / cardWidth);
      setCurrentIndex(index);
    }
  };

  return (
    <Box sx={{ width: "100%", bgcolor }}>
      <Container 
        component="section" 
        maxWidth="lg" 
        sx={{ 
          maxWidth: "1000px !important",
          py: { xs: 4, md: 7 },
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <SectionHeader 
          title="Our Completed Jobs"
        />
        
        <Box sx={{ position: "relative" }}>
          {/* Carousel Container */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollBehavior: "smooth",
              gap: 2,
              pb: 2,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {cleaningJobsImages.map((image, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: { xs: "100%", md: "calc(33.333% - 11px)" },
                  scrollSnapAlign: "start",
                }}
              >
                <Box
                  component="img"
                  src={s3BaseUrl + image}
                  alt={`Completed job ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: { xs: 250, md: 280 },
                    objectFit: "cover",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "transform 0.3s ease",
                    "&:hover": { 
                      transform: "scale(1.03)",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)"
                    },
                  }}
                  onClick={() => handleOpen(s3BaseUrl + image)}
                />
              </Box>
            ))}
          </Box>

          {/* Navigation Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              onClick={() => scrollToCard("left")}
              sx={{
                minWidth: 40,
                height: 40,
                borderRadius: "50%",
                p: 0,
              }}
            >
              <ChevronLeftIcon />
            </Button>

            {/* Progress indicator */}
            <Box
              sx={{
                height: 4,
                width: 100,
                bgcolor: "grey.200",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  bgcolor: "primary.main",
                  width: `${((currentIndex + (isMobile ? 1 : 3)) / cleaningJobsImages.length) * 100}%`,
                  transition: "width 0.3s ease",
                }}
              />
            </Box>

            <Button
              onClick={() => scrollToCard("right")}
              sx={{
                minWidth: 40,
                height: 40,
                borderRadius: "50%",
                p: 0,
              }}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>

        {/* Image Popup Modal */}
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90vw",
              height: "auto",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              borderRadius: "8px",
              cursor: "pointer",
            }}
            onClick={handleClose}
          >
            <Box
              component="img"
              src={selectedImage}
              alt="Expanded Cleaning Job"
              sx={{ 
                width: "100%", 
                height: "auto", 
                maxHeight: "90vh", 
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </Box>
        </Modal>
      </Container>
    </Box>
  );
}

export default CleaningJobsCarousel;
